import { createContext } from 'react';
import type { Session, User } from '@supabase/supabase-js';

/**
 * Auth context value.
 *
 * Kept in its own module so `AuthProvider.tsx` only exports a component —
 * Fast Refresh breaks when a file mixes components with non-component exports.
 */

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  /** Current auth resolution. `loading` until the first session check settles. */
  status: AuthStatus;
  session: Session | null;
  user: User | null;
  /** Convenience: true once status is no longer `loading`. */
  isReady: boolean;
  isAuthenticated: boolean;

  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUpWithPassword: (email: string, password: string, fullName: string) => Promise<void>;
  signInWithOAuth: (provider: 'google' | 'github') => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** The current access token, or null when signed out. Used by the API client. */
  getAccessToken: () => Promise<string | null>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
